import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bar, Doughnut } from 'react-chartjs-2'
import { FaChartBar, FaCoins, FaPlus, FaArrowRight } from 'react-icons/fa'
import api from '../../../../helpers/apiRequester'
import { StatTile } from '../../../reusable'
import { brandPalette, brandPaletteAlpha, formatCurrencyGBP, formatNumberGBP } from '../chartHelpers'
import styles from '../styles.module.scss'

interface FundingBatch {
    batch_id: string
    allocation_date?: string
    created_at?: string
    total_clients: number
    total_claims: number
    book_value?: number
    draw_down_value?: number
    introducer?: string
    allocated?: number
    submitted?: number
    api_transferred?: number
    config?: {
        ate_requested?: boolean
        ate_returned?: boolean
        nera_id?: string
    }
}

type BatchStage = 'API Transferred' | 'Submitted' | 'ATE Returned' | 'ATE Requested' | 'Pending'

const getBatchStage = (batch: FundingBatch): BatchStage => {
    if ((batch.api_transferred ?? 0) > 0) return 'API Transferred'
    if ((batch.submitted ?? 0) > 0) return 'Submitted'
    if (batch.config?.ate_returned) return 'ATE Returned'
    if (batch.config?.ate_requested) return 'ATE Requested'
    return 'Pending'
}

const stageOrder: BatchStage[] = ['API Transferred', 'Submitted', 'ATE Returned', 'ATE Requested', 'Pending']

export const FundingSection = () => {
    const navigate = useNavigate()
    const [batches, setBatches] = useState<FundingBatch[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            try {
                const response = await api({ url: '/funding-batches', data: {} })
                if (cancelled) return
                if (response.data?.success) {
                    const list: FundingBatch[] = (response.data.batches || []).slice().sort(
                        (a: FundingBatch, b: FundingBatch) => {
                            const aT = new Date(a.created_at || a.allocation_date || 0).getTime()
                            const bT = new Date(b.created_at || b.allocation_date || 0).getTime()
                            return bT - aT
                        },
                    )
                    setBatches(list)
                } else {
                    setBatches([])
                }
            } catch (err: any) {
                if (!cancelled) {
                    setError(err?.message || 'Failed to load funding batches')
                    setBatches([])
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [])

    const stats = useMemo(() => {
        const totalBatches = batches.length
        const totalDrawDown = batches.reduce((sum, b) => sum + (b.draw_down_value || 0), 0)
        const totalClients = batches.reduce((sum, b) => sum + (b.total_clients || 0), 0)
        const apiTransferred = batches.filter((b) => (b.api_transferred ?? 0) > 0).length
        return { totalBatches, totalDrawDown, totalClients, apiTransferred }
    }, [batches])

    const stageCounts = useMemo(() => {
        const counts: Record<BatchStage, number> = {
            'API Transferred': 0,
            Submitted: 0,
            'ATE Returned': 0,
            'ATE Requested': 0,
            Pending: 0,
        }
        batches.forEach((b) => {
            counts[getBatchStage(b)] += 1
        })
        return counts
    }, [batches])

    const doughnutData = useMemo(
        () => ({
            labels: stageOrder,
            datasets: [
                {
                    data: stageOrder.map((stage) => stageCounts[stage]),
                    backgroundColor: brandPalette.slice(0, stageOrder.length),
                    borderColor: '#ffffff',
                    borderWidth: 2,
                },
            ],
        }),
        [stageCounts],
    )

    const recentBatches = useMemo(() => batches.slice(0, 5), [batches])

    const barData = useMemo(
        () => ({
            labels: recentBatches.map((b) => b.batch_id || '—'),
            datasets: [
                {
                    label: 'Draw-down (£)',
                    data: recentBatches.map((b) => b.draw_down_value || 0),
                    backgroundColor: brandPaletteAlpha(0.7)[0],
                    borderColor: brandPalette[0],
                    borderWidth: 1,
                    borderRadius: 6,
                },
            ],
        }),
        [recentBatches],
    )

    return (
        <section className={styles.sectionCard}>
            <div className={styles.sectionHeader}>
                <div>
                    <h2 className={styles.sectionTitle}>
                        <FaCoins /> Funding
                    </h2>
                    <p className={styles.sectionSubtitle}>
                        Track funding batches across the allocation pipeline.
                    </p>
                </div>
                <div className={styles.sectionActions}>
                    <button className={styles.primaryAction} onClick={() => navigate('/collect-funding-clients')}>
                        <FaPlus /> Create a Batch
                    </button>
                    <button className={styles.secondaryAction} onClick={() => navigate('/allocate-claims')}>
                        Funding Allocation <FaArrowRight />
                    </button>
                </div>
            </div>

            {error ? <div className={styles.errorBanner}>{error}</div> : null}

            <div className={styles.statsRow}>
                <StatTile title="Total Batches" stat={loading ? '—' : String(stats.totalBatches)} />
                <StatTile
                    title="Total Draw-down"
                    stat={loading ? '—' : formatCurrencyGBP(stats.totalDrawDown)}
                />
                <StatTile title="Clients in Batches" stat={loading ? '—' : formatNumberGBP(stats.totalClients)} />
                <StatTile title="API Transferred" stat={loading ? '—' : String(stats.apiTransferred)} />
            </div>

            <div className={styles.chartsRow}>
                <div className={styles.chartCard}>
                    <div className={styles.chartHeader}>
                        <FaChartBar /> Pipeline Stages
                    </div>
                    <div className={styles.chartCanvas}>
                        {!loading && stats.totalBatches > 0 ? (
                            <Doughnut
                                data={doughnutData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { position: 'right', labels: { boxWidth: 12 } },
                                        tooltip: {
                                            callbacks: {
                                                label: (ctx) => `${ctx.label}: ${ctx.parsed} batch(es)`,
                                            },
                                        },
                                    },
                                    cutout: '60%',
                                }}
                            />
                        ) : (
                            <div className={styles.chartPlaceholder}>
                                {loading ? 'Loading…' : 'No batches yet'}
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.chartCard}>
                    <div className={styles.chartHeader}>
                        <FaChartBar /> Recent Batches by Draw-down
                    </div>
                    <div className={styles.chartCanvas}>
                        {!loading && recentBatches.length > 0 ? (
                            <Bar
                                data={barData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { display: false },
                                        tooltip: {
                                            callbacks: {
                                                label: (ctx) => formatCurrencyGBP(ctx.parsed.y ?? 0),
                                            },
                                        },
                                    },
                                    scales: {
                                        y: {
                                            beginAtZero: true,
                                            ticks: {
                                                callback: (value) =>
                                                    typeof value === 'number'
                                                        ? formatCurrencyGBP(value)
                                                        : value,
                                            },
                                        },
                                    },
                                }}
                            />
                        ) : (
                            <div className={styles.chartPlaceholder}>
                                {loading ? 'Loading…' : 'No batches to chart'}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.recentList}>
                <div className={styles.recentListTitle}>Recent batches</div>
                {loading ? (
                    <div className={styles.placeholderRow}>Loading…</div>
                ) : recentBatches.length === 0 ? (
                    <div className={styles.placeholderRow}>No batches found</div>
                ) : (
                    recentBatches.slice(0, 3).map((batch) => {
                        const stage = getBatchStage(batch)
                        return (
                            <button
                                key={batch.batch_id}
                                className={styles.recentRow}
                                onClick={() => navigate(`/funding-batch/${batch.batch_id}`)}
                            >
                                <span className={styles.recentBatchId}>{batch.batch_id}</span>
                                <span className={`${styles.stageChip} ${styles[`stage_${stage.replace(/\s+/g, '')}`]}`}>
                                    {stage}
                                </span>
                                <span className={styles.recentMeta}>
                                    {formatNumberGBP(batch.total_clients || 0)} clients
                                </span>
                                <span className={styles.recentMeta}>
                                    {formatCurrencyGBP(batch.draw_down_value || 0)}
                                </span>
                                <FaArrowRight className={styles.recentArrow} />
                            </button>
                        )
                    })
                )}
            </div>
        </section>
    )
}
